import type { PermissionCode } from '@dob/domain';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api } from '../api/client.ts';
import type { Profile, SignedInResponse } from '../api/types.ts';
import { setLang } from '../i18n.ts';

type Status = 'loading' | 'signedOut' | 'signedIn';

interface SessionValue {
  readonly status: Status;
  /** Set when the session ended on its own (expired, revoked elsewhere). */
  readonly endedUnexpectedly: boolean;
  readonly profile: Profile | undefined;
  readonly signedIn: (response: SignedInResponse) => void;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [endedUnexpectedly, setEndedUnexpectedly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api.resume().then((ok) => {
      if (!cancelled) setStatus(ok ? 'signedIn' : 'signedOut');
    });
    const stop = api.onSignedOut(() => {
      queryClient.clear();
      setEndedUnexpectedly(true);
      setStatus('signedOut');
    });
    return () => {
      cancelled = true;
      stop();
    };
  }, [queryClient]);

  const profile = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Profile>('/auth/me'),
    enabled: status === 'signedIn',
    staleTime: 60_000,
  });

  // The first time the profile arrives in a browser with no saved language, use the person's own.
  useEffect(() => {
    const preferred = profile.data?.user.preferredLang;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem('dob.lang');
    } catch {
      // ignore
    }
    if (preferred && !saved) void setLang(preferred);
  }, [profile.data?.user.preferredLang]);

  const signedIn = useCallback(
    (response: SignedInResponse) => {
      api.acceptTokens(response);
      queryClient.clear();
      setEndedUnexpectedly(false);
      setStatus('signedIn');
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await api.logout();
    queryClient.clear();
    setEndedUnexpectedly(false);
    setStatus('signedOut');
  }, [queryClient]);

  const value = useMemo<SessionValue>(
    () => ({ status, endedUnexpectedly, profile: profile.data, signedIn, signOut }),
    [status, endedUnexpectedly, profile.data, signedIn, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

// ── Current location ────────────────────────────────────────────────────────

/** `null` = company-wide view (only for people with a company-wide role). */
export type LocationChoice = string | null;

const LocationContext = createContext<{
  readonly locationId: LocationChoice;
  readonly companyWide: boolean;
  readonly setLocationId: (id: LocationChoice) => void;
} | null>(null);

function storageKey(userId: string) {
  return `dob.location.${userId}`;
}

export function LocationProvider({ children }: { readonly children: ReactNode }) {
  const { profile } = useSession();
  const companyWide = profile?.roles.some((r) => r.scope === 'ALL') ?? false;
  const [chosen, setChosen] = useState<LocationChoice | undefined>(undefined);

  // Default: company-wide for company roles, otherwise the first own location; a saved choice wins
  // while it is still allowed.
  const locationId = useMemo<LocationChoice>(() => {
    if (!profile) return null;
    const allowed = new Set(profile.locations.map((l) => l.id));
    let saved: string | null | undefined = chosen;
    if (saved === undefined) {
      try {
        saved = localStorage.getItem(storageKey(profile.user.id)) ?? undefined;
      } catch {
        saved = undefined;
      }
    }
    if (saved === '' && companyWide) return null;
    if (saved && allowed.has(saved)) return saved;
    return companyWide ? null : (profile.locations[0]?.id ?? null);
  }, [profile, chosen, companyWide]);

  const setLocationId = useCallback(
    (id: LocationChoice) => {
      setChosen(id);
      if (profile) {
        try {
          localStorage.setItem(storageKey(profile.user.id), id ?? '');
        } catch {
          // ignore
        }
      }
    },
    [profile],
  );

  const value = useMemo(
    () => ({ locationId, companyWide, setLocationId }),
    [locationId, companyWide, setLocationId],
  );
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useCurrentLocation() {
  const value = useContext(LocationContext);
  if (!value) throw new Error('useCurrentLocation outside LocationProvider');
  return value;
}

// ── Permissions ─────────────────────────────────────────────────────────────

interface PermissionsResponse {
  readonly permissions: readonly string[];
}

/**
 * What the person may do at the selected location (plus company-wide). The UI only hides what is
 * not allowed; the server checks every request anyway (CLAUDE.md §3.2.1).
 */
export function usePermissions() {
  const { locationId } = useCurrentLocation();
  const { status } = useSession();
  const enabled = status === 'signedIn';
  const companyWide = useQuery({
    queryKey: ['permissions', null],
    queryFn: () => api.get<PermissionsResponse>('/auth/me/permissions'),
    enabled,
    staleTime: 60_000,
  });
  const atLocation = useQuery({
    queryKey: ['permissions', locationId],
    queryFn: () =>
      api.get<PermissionsResponse>('/auth/me/permissions', { locationId: locationId ?? undefined }),
    enabled: enabled && locationId !== null,
    staleTime: 60_000,
  });
  const set = useMemo(
    () =>
      new Set<string>([
        ...(companyWide.data?.permissions ?? []),
        ...(atLocation.data?.permissions ?? []),
      ]),
    [companyWide.data, atLocation.data],
  );
  const can = useCallback((code: PermissionCode) => set.has(code), [set]);
  /** Held through a company-wide role (routes the server checks with the `global` scope). */
  const canCompanyWide = useCallback(
    (code: PermissionCode) => companyWide.data?.permissions.includes(code) === true,
    [companyWide.data],
  );
  return {
    can,
    canCompanyWide,
    loading: companyWide.isPending || (locationId !== null && atLocation.isPending),
  };
}
